import { useMemo, useState } from "react";
import {
  useBridgeIncidents,
  useBridgeUptime,
  useTopProblemLots,
} from "../../hooks/useBridgeHistory";
import { useStatus } from "../../hooks/useStatus";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatDuration, fmtTime, lotLabel } from "../../lib/historyFormat";
import {
  DEFAULT_TIME_RANGE,
  timeRangeLabel,
  type TimeRange,
} from "../../lib/timeRange";
import {
  EmptyHint,
  Pager,
  FilterRow,
  SearchInput,
  SectionCard,
  SegToggle,
  TimeRangePicker,
  UptimeRow,
  useUptimeBarDays,
} from "./HistoryPrimitives";
import BridgeLotIncidentsModal from "./BridgeLotIncidentsModal";

const PAGE_SIZE = 10;
// 문제 많은 주차장은 넉넉히 받아 FE 에서 검색·페이지네이션 (전체 lot 수십 규모).
const PROBLEM_FETCH_SIZE = 100;

/** 주차장명/코드/사이트로 검색어 일치 여부. */
function matchesQuery(
  item: { lotName: string | null; lotId: string; siteKey: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    (item.lotName ?? "").toLowerCase().includes(q) ||
    item.lotId.toLowerCase().includes(q) ||
    item.siteKey.toLowerCase().includes(q)
  );
}

/**
 * 브릿지(DB헬스) 이력 — 주차장(lot) 단위. 층(floor) 세분화는 추후(현재 lot 단위 incident).
 * 데이터는 `/api/bridge-history/*` (DOWN incident/daily 집계).
 */
export default function BridgeHistorySections() {
  return (
    <div className="space-y-4">
      <Uptime />
      <ProblemLots />
      <Timeline />
    </div>
  );
}

function ProblemLots() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [selectedLot, setSelectedLot] = useState<{
    siteKey: string;
    lotId: string;
    lotName: string | null;
  } | null>(null);
  const { data, isLoading } = useTopProblemLots(range, 0, PROBLEM_FETCH_SIZE);
  const all = data?.items ?? [];
  const filtered = useMemo(
    () => all.filter((l) => matchesQuery(l, debouncedQuery)),
    [all, debouncedQuery],
  );
  const total = filtered.length;
  const start = page * PAGE_SIZE;
  const lots = filtered.slice(start, start + PAGE_SIZE);
  const hasNext = start + PAGE_SIZE < total;

  const onRange = (r: TimeRange) => {
    setRange(r);
    setPage(0);
  };
  const onSearch = (v: string) => {
    setQuery(v);
    setPage(0);
  };

  return (
    <SectionCard
      title="문제 많은 주차장"
      hint="총 정체 많은 순 · 데이터 정체(DOWN)"
      info={{
        summary:
          "선택 기간 동안 데이터 정체(DOWN)가 가장 잦았던 주차장을 누적 정체 시간 순으로 보여줍니다. 행을 클릭하면 상세 이력이 열립니다.",
        basis:
          "'정체(DOWN)' = 중앙 DB에 들어온 최신 데이터가 임계 시간(주간 30면↑ 2h / 30면↓ 3h · 야간 22~06시 6h) 이상 갱신되지 않은 사건(incident).",
        rows: [
          { term: "정체 N회", desc: "그 주차장이 DOWN 으로 진입한 횟수" },
          { term: "총 X", desc: "DOWN 상태로 있던 누적 시간" },
          { term: "회색 코드", desc: "사이트 키(siteKey)" },
        ],
        legend: [
          { swatch: "bg-status-majorOutage", label: "빨강 점 — 정체 집계 대상 표식" },
        ],
        period: "상단 기간 선택 기준 (기본 최근 30일 · 1~365일/사용자 지정)",
      }}
    >
      <FilterRow>
        <TimeRangePicker value={range} onChange={onRange} />
        <SearchInput value={query} onChange={onSearch} placeholder="주차장 검색 (이름/코드)" />
      </FilterRow>
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : total === 0 ? (
        <EmptyHint
          text={
            debouncedQuery
              ? "검색 결과가 없습니다."
              : `${timeRangeLabel(range)} 동안 데이터 정체가 기록되지 않았습니다.`
          }
        />
      ) : (
        <ul className="space-y-2">
          {lots.map((l) => (
            <li
              key={`${l.siteKey}-${l.lotId}`}
              tabIndex={0}
              role="button"
              aria-label={`${lotLabel(l.lotName, l.lotId)} 정체 이력 보기`}
              onClick={() =>
                setSelectedLot({
                  siteKey: l.siteKey,
                  lotId: l.lotId,
                  lotName: l.lotName,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedLot({
                    siteKey: l.siteKey,
                    lotId: l.lotId,
                    lotName: l.lotName,
                  });
                }
              }}
              className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-surface-border px-3 py-2 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-surface-darkBorder dark:hover:bg-slate-800/40"
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full bg-status-majorOutage dark:bg-status-majorOutage-dark"
                />
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {lotLabel(l.lotName, l.lotId)}
                </span>
                <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                  {l.siteKey}
                </span>
              </span>
              <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                정체 <span className="font-medium tabular-nums">{l.incidentCount}</span>회 ·
                총 <span className="font-medium">{formatDuration(l.totalDownSeconds)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {selectedLot && (
        <BridgeLotIncidentsModal
          lotId={selectedLot.lotId}
          lotName={selectedLot.lotName}
          siteKey={selectedLot.siteKey}
          onClose={() => setSelectedLot(null)}
        />
      )}
      {total > 0 && (
        <Pager
          page={page}
          size={PAGE_SIZE}
          total={total}
          hasNext={hasNext}
          onPage={setPage}
        />
      )}
    </SectionCard>
  );
}

function Uptime() {
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  // 서버에서는 90일치를 그대로 받고, 화면에 몇 일을 그릴지만 폭에 따라 달라진다
  // (모바일 30일). 헤더 문구도 같은 값을 써야 막대와 어긋나지 않는다.
  const barDays = useUptimeBarDays();
  const { data, isLoading } = useBridgeUptime(90);
  const lots = data ?? [];
  const filtered = useMemo(
    () => lots.filter((l) => matchesQuery(l, debouncedQuery)),
    [lots, debouncedQuery],
  );
  const total = filtered.length;
  const start = page * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);
  const hasNext = start + PAGE_SIZE < total;

  const onSearch = (v: string) => {
    setQuery(v);
    setPage(0);
  };

  return (
    <SectionCard
      title="주차장별 가동률"
      hint={`최근 ${barDays}일 · 막대 1개 = 하루`}
      info={{
        summary:
          "주차장이 하루 중 데이터가 정상 유입된(가동) 시간 비율입니다. 막대 1개 = 하루.",
        basis:
          "매일 자정 직후 전날의 데이터 정체(DOWN) 구간을 합산해 (86400초 − 정체초) / 86400 으로 계산합니다. DOWN = 최신 데이터가 임계 시간(주간 30면↑ 2h / 30면↓ 3h · 야간 22~06시 6h) 이상 갱신 안 됨. 막대를 누르거나 마우스를 올리면 그날의 정체 구간이 표시됩니다.",
        legend: [
          { swatch: "bg-status-operational", label: "초록 — 가동률 99.9% 이상" },
          { swatch: "bg-status-degraded", label: "주황 — 95% ~ 99.9%" },
          { swatch: "bg-status-majorOutage", label: "빨강 — 95% 미만" },
          { swatch: "bg-slate-200", label: "옅은 회색 — 기록 없음(집계 전·등록 전)" },
        ],
        period: `최근 ${barDays}일 고정 · KST 기준`,
      }}
    >
      <div className="mb-3">
        {/* 이 섹션은 검색창만 단독으로 놓이므로 모바일에서 한 줄을 다 쓴다. */}
        <SearchInput
          value={query}
          onChange={onSearch}
          placeholder="주차장 검색 (이름/코드)"
          fullWidth
        />
      </div>
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : total === 0 ? (
        <EmptyHint
          text={
            debouncedQuery
              ? "검색 결과가 없습니다."
              : "아직 일별 요약이 없습니다 (매일 자정 집계)."
          }
        />
      ) : (
        <div className="space-y-3">
          {pageItems.map((l) => (
            <UptimeRow
              key={`${l.siteKey}-${l.lotId}`}
              label={`${lotLabel(l.lotName, l.lotId)} · ${l.siteKey}`}
              days={l.days}
            />
          ))}
        </div>
      )}
      {total > 0 && (
        <Pager
          page={page}
          size={PAGE_SIZE}
          total={total}
          hasNext={hasNext}
          onPage={setPage}
        />
      )}
    </SectionCard>
  );
}

type StatusFilter = "all" | "ongoing" | "ended";
type RecentDaysFilter = 1 | 3 | 7 | 30 | 0; // 0 = 전체
type RecentLimitFilter = 20 | 50;

function withinDays(iso: string, days: number, now: number): boolean {
  if (days <= 0) return true;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return true;
  return now - ms <= days * 24 * 60 * 60 * 1000;
}

function Timeline() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [siteFilter, setSiteFilter] = useState<string>("all");
  const [daysFilter, setDaysFilter] = useState<RecentDaysFilter>(7);
  const [limitFilter, setLimitFilter] = useState<RecentLimitFilter>(20);
  // 사이트 필터 옵션은 /api/status 응답의 sites 로 동적 생성 — 사이트 추가 시 자동 반영.
  const { data: statusData } = useStatus();
  const siteOptions = useMemo(
    () => [
      { value: "all" as string, label: "전체" },
      ...(statusData?.sites ?? []).map((s) => ({
        value: s.siteKey,
        label: s.displayName,
      })),
    ],
    [statusData?.sites],
  );
  // raw 는 백엔드 상한(50) 으로 고정 — 클라이언트 필터 후 limit 만큼 잘라 표시.
  const { data, isLoading } = useBridgeIncidents(50);
  const raw = data ?? [];
  const now = Date.now();
  const incidents = raw
    .filter((i) => {
      if (statusFilter === "ongoing" && !i.ongoing) return false;
      if (statusFilter === "ended" && i.ongoing) return false;
      if (siteFilter !== "all" && i.siteKey !== siteFilter) return false;
      if (!withinDays(i.startedAt, daysFilter, now)) return false;
      return true;
    })
    .slice(0, limitFilter);

  return (
    <SectionCard
      title="최근 이력"
      hint="데이터 정체(DOWN) 구간"
      info={{
        summary: "데이터 정체(DOWN)가 발생했던 개별 구간을 최신순으로 보여줍니다.",
        basis:
          "각 줄 = 사건일지(incident) 한 건. 데이터가 임계 이상 끊긴 시각부터 복구까지의 구간입니다.",
        rows: [
          { term: "시작 ~ 종료", desc: "정체 시작 ~ 복구 시각 (MM/DD HH:mm)" },
          { term: "지속 시간", desc: "종료된 구간의 정체 길이" },
          { term: "회색 코드", desc: "사이트 키(siteKey)" },
        ],
        legend: [
          {
            swatch: "bg-status-majorOutage",
            label: "빨강 '진행 중' 배지 — 아직 복구 안 된 구간",
          },
        ],
        period:
          "필터: 기간(1/3/7/30/전체) · 상태 · 사이트 · 건수. 최근 50건 내에서 필터.",
      }}
    >
      <FilterRow>
        <SegToggle<RecentDaysFilter>
          value={daysFilter}
          onChange={setDaysFilter}
          options={[
            { value: 1, label: "1일" },
            { value: 3, label: "3일" },
            { value: 7, label: "7일" },
            { value: 30, label: "30일" },
            { value: 0, label: "전체" },
          ]}
          ariaLabel="기간 필터"
        />
        <SegToggle<StatusFilter>
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "전체" },
            { value: "ongoing", label: "진행 중" },
            { value: "ended", label: "종료" },
          ]}
          ariaLabel="상태 필터"
        />
        <SegToggle<string>
          value={siteFilter}
          onChange={setSiteFilter}
          options={siteOptions}
          ariaLabel="사이트 필터"
        />
        <SegToggle<RecentLimitFilter>
          value={limitFilter}
          onChange={setLimitFilter}
          options={[
            { value: 20, label: "20개" },
            { value: 50, label: "50개" },
          ]}
          ariaLabel="표시 건수"
        />
      </FilterRow>
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : incidents.length === 0 ? (
        <EmptyHint text="조건에 맞는 정체 구간이 없습니다." />
      ) : (
        <ul className="space-y-1.5">
          {incidents.map((i, idx) => (
            <li
              key={`${i.siteKey}-${i.lotId}-${i.startedAt}-${idx}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
            >
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {lotLabel(i.lotName, i.lotId)}
              </span>
              <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                {i.siteKey}
              </span>
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                {fmtTime(i.startedAt)} ~ {fmtTime(i.endedAt)}
              </span>
              {i.ongoing ? (
                <span className="rounded-full bg-status-majorOutage-bg px-2 py-0.5 text-[11px] font-medium text-status-majorOutage-fg dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg">
                  진행 중
                </span>
              ) : (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {formatDuration(i.durationSec)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
